"use client"

import React, { useState } from "react"
import { useUser } from "lib/user"
import { useBannerContent } from "components/Layouts/StickyFooter/StickyFooterMember/hooks"
import { Banner } from "basket_and_checkout/shared/components/Banner/Banner"
import { ExpanderButton } from "basket_and_checkout/shared/components/ExpanderButton/ExpanderButton"
import {
  membershipPerksStyles,
  membershipDetailsWrapperStyles,
  membershipPerksWrapperStyles,
  membershipSavingsStyles,
} from "./MembershipDetails.css"
import { useOverflowDetection } from "hooks/useOverflowDetection"
import type { MembershipOption } from "@shared/graphql.types"
import { useFragment } from "@apollo/client"
import { MEMBERSHIP_TOGGLE_CARD_FRAGMENT } from "lib/apollo/fragments"

export const MembershipDetails: React.FC<{
  variant?: string
  membershipCardDetails: MembershipOption
}> = ({ variant, membershipCardDetails }) => {
  const { data } = useFragment({
    fragment: MEMBERSHIP_TOGGLE_CARD_FRAGMENT,
    fragmentName: "MembershipToggleCardFragment",
    from: {
      __typename: "BasketMembershipToggleCardType",
      id: "basket-membership-toggle",
    },
  })
  const { showToggle, withinAccordionDescription } = data

  const showInDrawer = variant === "atbConfirmation"

  const [showMore, setShowMore] = useState(
    showInDrawer ? !!membershipCardDetails : false
  )
  const { bannerContent } = useBannerContent("miniBasket")
  const { hasMembership } = useUser()

  const toggleShowMore = () => setShowMore((prev) => !prev)

  const { divRef, hasOverflow } = useOverflowDetection(showInDrawer, showMore)

  // Toggle is visible to user. No need to render details. Details is rendered in toggle.
  const isToggleVisibleToUser = !showInDrawer && showToggle

  if ((!membershipCardDetails && !hasMembership) || isToggleVisibleToUser) {
    return null
  }

  return (
    <div
      data-testid="MembershipDetails"
      ref={divRef}
      className={
        membershipDetailsWrapperStyles[showMore ? "collapsed" : "expanded"]
      }
    >
      {hasMembership && showInDrawer && (
        <div className={membershipSavingsStyles}>
          <Banner bannerText={bannerContent} />
        </div>
      )}
      {withinAccordionDescription && (
        <div
          className={
            membershipPerksWrapperStyles[showInDrawer ? "inDrawer" : "default"]
          }
        >
          <div
            id="membership-perks"
            className={membershipPerksStyles}
            dangerouslySetInnerHTML={{
              __html: withinAccordionDescription,
            }}
          />
        </div>
      )}
      {hasOverflow && (
        <ExpanderButton toggleShowMore={toggleShowMore} showMore={showMore} />
      )}
    </div>
  )
}
